import { useState, useEffect, useCallback, useRef } from "react";
import LocalLink from "@/components/LocalLink";
import { useCopy } from "@/lib/i18n/copy";
import { HERO } from "@/content/pages/home";

const BASE = "https://eventboutique.pt/img";

// Подписи alt уехали в контент-слой (src/content/pages/home.ts): это текст для
// человека и он локализуется. Порядок файлов и порядок подписей связаны индексом.
const slides = [
  { file: "header-01.webp" },
  { file: "header-02.webp" },
  { file: "header-03.webp" },
  { file: "header-04.webp" },
  { file: "header-254.webp" },
];

/**
 * Hero image quality — two tiers (PSI mobile audit 2026-07-11).
 * Slide 0 (LCP): 480 q80 / 768 q84 / 1200 q88 / 1920 q90 — URLs must stay
 * 1:1 in sync with the responsive preload in index.html.
 * Later slides sit behind Ken Burns motion + the gradient overlay, so a
 * lower tier is visually invisible: 480 q75 / 768 q78 / 1200 q82 / 1920 q84.
 * The old q95/q98 "near-lossless" tiers cost 300-400 KiB per slide on mobile.
 */
const heroSrc = (file: string, width: number, q = 90) => `${BASE}/${file}?width=${width}&quality=${q}`;
const heroSrcSet = (file: string, lcp: boolean) =>
  lcp
    ? `${heroSrc(file, 480, 80)} 480w, ${heroSrc(file, 768, 84)} 768w, ${heroSrc(file, 1200, 88)} 1200w, ${heroSrc(file, 1920, 90)} 1920w`
    : `${heroSrc(file, 480, 75)} 480w, ${heroSrc(file, 768, 78)} 768w, ${heroSrc(file, 1200, 82)} 1200w, ${heroSrc(file, 1920, 84)} 1920w`;

// Slide pacing — luxury feel: longer dwell time, slower fade
const INTERVAL = 6000;   // slide visible for 6s
const FADE_MS = 1600;    // crossfade 1.6s — soft cinematic transition

// Ken Burns zoom — must outlive the slide's full visible life:
// fade-in starts at t=0, fade-out completes at INTERVAL + FADE_MS.
// Extra 600ms buffer guarantees the zoom never halts while still visible.
const ZOOM_MS = INTERVAL + FADE_MS + 600;

const HeroSection = () => {
  const { t, fb } = useCopy(HERO);
  // prev tracks the outgoing slide so its zoom keeps running through the
  // entire crossfade — resetting transform mid-fade is the classic KB glitch.
  const [slide, setSlide] = useState({ current: 0, prev: -1 });
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  // Slides 1+ mount their <img> only after the LCP window has passed:
  // opacity:0 does NOT defer loading, so five stacked full-viewport images
  // otherwise all download at once on mobile and compete with the LCP slide.
  // 3.5s arm gives the first crossfade (t=6s) a 2.5s head start.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setArmed(true), 3500);
    return () => clearTimeout(t);
  }, []);

  const advance = useCallback(() => {
    setSlide((s) => ({ current: (s.current + 1) % slides.length, prev: s.current }));
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(advance, INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [advance]);

  // Release the outgoing slide only AFTER its fade-out fully completes —
  // its zoom animation is then removed (and resets) while opacity is 0.
  useEffect(() => {
    if (slide.prev < 0) return;
    const t = setTimeout(() => setSlide((s) => ({ ...s, prev: -1 })), FADE_MS + 200);
    return () => clearTimeout(t);
  }, [slide.current, slide.prev]);

  const current = slide.current;

  return (
    <section className="relative w-full overflow-hidden film-grain hero-section" data-header-theme="hero" {...fb}>
      {/* Dark base */}
      <div className="absolute inset-0 bg-[hsl(0,0%,8%)]" />

      {/* All slides mounted from initial render — browser handles native lazy-loading.
          Crossfade + continuous Ken Burns zoom as ONE motion: the zoom starts with the
          fade-in and keeps running through the entire fade-out (i === current || prev),
          so the dissolve and the drift read as a single cinematic movement.
          Transform-only (GPU compositor), linear pace — no mid-fade resets. */}
      {slides.map((s, i) => {
        const zooming = i === slide.current || i === slide.prev;
        return (
          <div
            key={i}
            className="absolute inset-0 overflow-hidden"
            style={{
              opacity: i === current ? 1 : 0,
              transition: `opacity ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              zIndex: i === current ? 1 : 0,
              willChange: "opacity",
            }}
          >
            {(i === 0 || armed) && (
              <img
                src={heroSrc(s.file, 1600, i === 0 ? 90 : 82)}
                srcSet={heroSrcSet(s.file, i === 0)}
                sizes="100vw"
                alt={t.slideAlts[i]}
                className="w-full h-full object-cover hero-kb"
                style={{
                  animation: zooming ? `heroKenBurns ${ZOOM_MS}ms linear forwards` : "none",
                }}
                width={1600}
                height={1067}
                // Как и на BTQ: слайды 2+ появляются в DOM только после `armed`,
                // поэтому ленивая загрузка тут уже ничего не экономит — она лишь
                // откладывает файл до момента показа, и зритель видит пустой кадр.
                fetchPriority={i === 0 ? "high" : "low"}
                loading="eager"
                decoding={i === 0 ? "sync" : "async"}
              />
            )}
          </div>
        );
      })}

      <div className="absolute inset-0 gradient-overlay z-[1]" />

      <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 pt-32 md:pt-40 luxury-container">
        <p className="text-caption text-primary-foreground/70 mb-5 md:mb-4">
          {t.eyebrow}
        </p>

        <h1 className="text-editorial text-display text-primary-foreground mb-4 max-w-4xl">
          {t.h1}
        </h1>

        <p className="text-editorial text-primary-foreground/70 text-base md:text-lg mb-6 font-light tracking-[0.02em]">
          {t.tagline}
        </p>

        <p
          className="text-primary-foreground/80 text-sm md:text-base max-w-2xl mb-8 font-light leading-relaxed animate-hero-fade-in"
          style={{ animationDelay: "0.4s" }}
        >
          {t.lede}
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 animate-hero-fade-in"
          style={{ animationDelay: "0.6s" }}
        >
          <LocalLink
            to="/portfolio/"
            className="inline-flex items-center justify-center border border-primary-foreground/60 text-primary-foreground px-8 py-4 text-caption hover-lift hover:bg-primary-foreground/10 transition-colors"
          >
            {t.ctaWork}
          </LocalLink>
          <LocalLink
            to="/contact/"
            className="inline-flex items-center justify-center bg-primary-foreground text-foreground px-8 py-4 text-caption hover-lift"
          >
            {t.ctaPlanning}
          </LocalLink>
        </div>
      </div>

      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-hero-bounce"
      >
        <div className="w-px h-12 bg-primary-foreground/30" />
      </div>
    </section>
  );
};

export default HeroSection;
